import { ApiResponse } from '@interfaces/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

export const focusAreasApiHandler = {
  getRecommendedFocusAreas: async (
    assessmentId: number,
  ): Promise<ApiResponse<RecommendedFocusArea[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-recommended-focus-areas/${assessmentId}`,
    ),

  addFocusAreas: async (payload: AddFocusAreasPayload): Promise<ApiResponse<FocusArea[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/add-focus-areas`, payload),

  getActiveFocusAreas: async (): Promise<ApiResponse<ActiveFocusArea[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(`/get-active-focus-areas`),

  removeFocusArea: async (focusAreaId: number): Promise<ApiResponse<string>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).delete(`/remove-focus-area/${focusAreaId}`),
};

export interface Questionnaire {
  id: number;
  name: string;
  shortDescription: string;
  description: string;
  version: string;
  type: string;
  default: boolean;
  scoringType: string;
  scoreCalculationType: string;
  outputType: string;
  questionnaireType: string;
  factorSetId: number;
  color: string;
  icon: string;
  ewsTriggeredBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface QuestionnairesV2Categories {
  id: number;
  questionnaireId: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: number;
  userAssessmentId: number;
  categoryId: number;
  totalScore: number;
  createdAt: string;
  updatedAt: string;
  questionnairesV2Categories: QuestionnairesV2Categories;
}

export interface Question {
  id: number;
  questionnaireId: number;
  categoryId: number;
  responseScaleId: number;
  questionText: string;
  orderId: number;
  required: boolean;
  icon: string;
  label: string;
  buttonText: string;
  description: string;
  isBrakeOff: boolean;
  focusAreaWeight: number;
  createdAt: string;
  updatedAt: string;
}

export interface RecommendedFocusArea {
  questionnaire: Questionnaire;
  category: Category;
  question: Question;
  assessmentId: number;
  focusAreaPriorityScore: number;
  focusAreaConcernPercentage: number;
  active: boolean;
}

export interface FocusAreaPayload {
  questionV2Id: number;
  concernPercentage: number;
  priorityScore: number;
}

export interface AddFocusAreasPayload {
  userAssessmentId: number;
  focusAreas: FocusAreaPayload[];
}

export interface FocusArea {
  id: number;
  questionV2Id: number;
  userId: number;
  userAssessmentId: number;
  active: boolean;
  concernPercentage: number;
  priorityScore: number;
  createdAt: string;
  updatedAt: string;
}

export interface ActiveFocusArea {
  id: number;
  questionV2Id: number;
  userId: number;
  userAssessmentId: number;
  active: boolean;
  concernPercentage: number;
  priorityScore: number;
  createdAt: string;
  updatedAt: string;
  questionnairesV2Questions: Question;
  questionnaire: Questionnaire;
  category: Category;
}

export const useFocusAreasHooks = {
  // GET /get-recommended-focus-areas/{assessmentId}
  useRecommendedFocusAreas: (assessmentId: number) =>
    useQuery({
      queryKey: ['recommended-focus-areas', assessmentId],
      queryFn: () => focusAreasApiHandler.getRecommendedFocusAreas(assessmentId),
      enabled: !!assessmentId || assessmentId !== 0, // Only run query if assessmentId exists
      // staleTime: 5 * 60 * 1000, // 5 minutes
    }),

  // GET /get-active-focus-areas
  useActiveFocusAreas: () =>
    useQuery({
      queryKey: ['active-focus-areas'],
      queryFn: () => focusAreasApiHandler.getActiveFocusAreas(),
      // staleTime: 2 * 60 * 1000, // 2 minutes
    }),

  // POST /add-focus-areas
  useAddFocusAreas: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (payload: AddFocusAreasPayload) => focusAreasApiHandler.addFocusAreas(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['active-focus-areas'] });
        queryClient.invalidateQueries({ queryKey: ['recommended-focus-areas'] });
      },
      onError: (error) => {
        console.error('Failed to add focus areas:', error);
      },
    });
  },

  // DELETE /remove-focus-area/{focusAreaId}
  useRemoveFocusArea: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (focusAreaId: number) => focusAreasApiHandler.removeFocusArea(focusAreaId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['active-focus-areas'] });
      },
      onError: (error) => {
        console.error('Failed to remove focus area:', error);
      },
    });
  },

  useBatchRemoveFocusArea: () =>
    useMutation({
      mutationFn: (focusAreaId: number) => focusAreasApiHandler.removeFocusArea(focusAreaId),
    }),
};
